import { useCallback } from "react";

import { createReference } from "@medplum/core";
import { ClinicalImpression, Practitioner } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { showNotification } from "@mantine/notifications";

import {
  ClientNoteForm,
  ClientNoteStatus,
  ClientNoteTypeForm,
  updateClientNoteContent,
} from "@/clients/zod-schemas/client-note";
import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";

import { NoteEditorForm } from "../NoteEditorForm";

interface EditClientConfidentialNoteProps {
  clinicalImpression: ClinicalImpression;
  handleToggleEdit: () => void;
  setRefresh: (refresh: boolean | ((prev: boolean) => boolean)) => void;
  background?: string;
}

export default function EditClientConfidentialNote({
  clinicalImpression,
  handleToggleEdit,
  setRefresh,
  background,
}: EditClientConfidentialNoteProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const { summary } = getClinicalImpressionData(clinicalImpression);

  const handleEditNoteSubmit = useCallback(
    async (formValues: ClientNoteTypeForm) => {
      try {
        await updateClientNoteContent({
          medplum,
          clinicalImpression,
          form: formValues,
          practitionerReference: createReference(profile),
        });
        setRefresh((prev) => !prev);
        handleToggleEdit();
        showNotification({
          color: "green",
          message: "Confidential note updated successfully",
        });
      } catch (err) {
        showNotification({
          color: "red",
          message: "Failed to update confidential note",
          autoClose: false,
          styles: { description: { whiteSpace: "pre-line" } },
        });
      }
    },
    [medplum, clinicalImpression, profile, handleToggleEdit, setRefresh],
  );

  return (
    <NoteEditorForm<ClientNoteTypeForm>
      bg={background}
      submitLabel="Update"
      schema={ClientNoteForm}
      className="mb-0 sm:mb-xs"
      onCancel={handleToggleEdit}
      onSubmit={handleEditNoteSubmit}
      initialValues={{
        noteStatus: clinicalImpression.status as ClientNoteStatus,
        noteText: summary || "",
      }}
      testIds={{
        text: "edit-confidential-note-text",
        cancel: "cancel-edit-confidential-note-button",
        submit: "update-confidential-note-button",
      }}
    />
  );
}
